import { EV } from '../core/eventbus.js';
import { Repo } from '../data/schema.js';
import { getItem } from '../data/items.js';
import { Tags } from '../systems/tags.js';
import { Assemble } from '../systems/assemble.js';
import { Vehicle } from '../systems/vehicle.js';
import {
  STAGES, STAGE_COUNT, totalNeed, remainingNeed, VEHICLE_DEFS,
} from '../data/vehicles.js';

/**
 * 攒车那块面板：工作台前、车铺工位前按 E 都会开它。
 *
 * 面板不替玩家做决定：缺料就写清楚缺几，失手概率摆在那儿，
 * 按钮按下去是「我把这一段装上」，不是「帮我自动攒车」。
 */
export class GaragePanel {
  constructor(engine, { toast } = {}) {
    this.engine = engine;
    this.db = engine.db;
    this.toast = toast || ((text, kind) => engine.events.emit(EV.TOAST, { text, kind }));
    this.bench = 'none';
    this.propType = '';
    // 读条：正在拧的那一段。面板关了也照样走，回来还能看见进度
    this.busy = null;

    this.el = {
      root: document.getElementById('assemble'),
      title: document.getElementById('assemble-title'),
      info: document.getElementById('assemble-info'),
      stages: document.getElementById('assemble-stages'),
      need: document.getElementById('assemble-need'),
      acts: document.getElementById('assemble-acts'),
      work: document.getElementById('assemble-work'),
      workLabel: document.getElementById('assemble-work-label'),
      workBar: document.getElementById('assemble-work-bar'),
      workNum: document.getElementById('assemble-work-num'),
    };
    if (!this.el.root) return;      // Node 里跑冒烟测试没有 DOM，安静退场
    this.bind();
  }

  bind() {
    for (const el of document.querySelectorAll('[data-assemble-close]')) {
      el.onclick = () => this.close();
    }
    this.el.acts.addEventListener('click', (e) => {
      const btn = e.target && e.target.closest ? e.target.closest('[data-act]') : null;
      if (!btn) return;
      this.doAct(btn.dataset.act);
    });
    this.engine.events.on(EV.GARAGE_OPEN, ({ bench, propType } = {}) => this.open(bench, propType));
  }

  open(bench = 'none', propType = '') {
    if (!this.el.root) return;
    this.bench = bench || 'none';
    this.propType = propType || '';
    this.el.root.classList.remove('hidden');
    this.render();
  }

  close() {
    if (this.el.root) this.el.root.classList.add('hidden');
  }

  isOpen() {
    return !!this.el.root && !this.el.root.classList.contains('hidden');
  }

  say(res) {
    if (!res) return;
    if (res.ok) this.toast(res.text, 'good');
    else this.toast(res.reason, 'warn');
  }

  /* ------------------------------ 读条 ------------------------------ */

  /**
   * 每帧由 main.js 带着走（暂停就不走），步子越小条越匀。
   * 面板开着关着都推进：按下扳手的是人，不是面板。
   */
  update(dt) {
    if (!this.busy) return;
    this.busy.left -= dt;
    if (this.busy.left > 0) { this.paintWork(); return; }
    this.finishWork();
  }

  /** 开工前先盘算一遍：缺料、手抖这些当场就说话，不让白拧一条 */
  startWork() {
    if (this.busy) { this.toast('扳手还在抡，先把这一段拧完。', 'warn'); return; }
    const plan = Assemble.planWork(this.db, { bench: this.bench });
    if (!plan.ok) { this.say({ ok: false, reason: plan.reason }); this.render(); return; }
    this.busy = {
      index: plan.index, stage: plan.stage, bench: plan.benchId,
      sec: plan.sec, left: plan.sec,
    };
    this.render();
    this.paintWork();
  }

  /** 住手：这一段没结算，料与手劲都还在 */
  cancelWork() {
    if (!this.busy) return;
    const name = this.busy.stage.name;
    this.busy = null;
    this.toast(`把手从${name}上挪开了，这一段没动。`, 'warn');
    this.render();
  }

  finishWork() {
    const work = this.busy;
    if (!work) return;
    this.busy = null;          // 先摘掉，免得 install 出岔子把人永久卡在读条里
    this.render();
    this.say(Assemble.install(this.db, { bench: work.bench }));
    this.render();
  }

  /** 条走到哪儿了（只动那几个数字，不动列表） */
  paintWork() {
    const b = this.busy;
    if (!b) return;
    const pct = b.sec > 0 ? Math.max(0, Math.min(1, 1 - b.left / b.sec)) : 1;
    if (this.el.workBar) this.el.workBar.style.width = `${(pct * 100).toFixed(1)}%`;
    if (this.el.workNum) this.el.workNum.textContent = `${Math.round(pct * 100)}%`;
  }

  renderWork() {
    if (!this.el.work) return;
    if (!this.busy) { this.el.work.classList.add('hidden'); return; }
    this.el.work.classList.remove('hidden');
    if (this.el.workLabel) this.el.workLabel.textContent = `正在装${this.busy.stage.name}`;
    this.paintWork();
  }

  doAct(act) {
    const db = this.db;
    if (act === 'begin') this.say(Assemble.begin(db, { scene: Repo.getPlayer(db).scene }));
    else if (act === 'install') this.startWork();
    else if (act === 'stop') this.cancelWork();
    else if (act === 'scrap') {
      this.busy = null;
      this.say(Assemble.scrap(db));
    } else if (act === 'refuel') this.say(Vehicle.refuel(db));
    else if (act === 'repair') this.say(Vehicle.repair(db));
    else if (act === 'upgrade') this.say(Vehicle.upgrade(db));
    this.render();
  }

  render() {
    if (!this.el.root || this.el.root.classList.contains('hidden')) return;
    const db = this.db;
    const row = Assemble.current(db);
    const car = Assemble.firstCar(db);
    const bench = Assemble.bench(this.bench);
    const skill = Tags.get(db, 'machinery').total;

    this.el.title.textContent = row ? '攒车' : (car ? '我的车' : '攒车');
    this.el.info.innerHTML = [
      `<div>工位：${bench.name} — ${bench.desc}</div>`,
      `<div>机械手艺：${skill}　失手概率：${Math.round(Assemble.failRate(db, this.bench) * 100)}%</div>`,
    ].join('');
    this.renderWork();

    if (row) {
      this.renderStages(row);
      this.renderNeed(row);
      this.renderActs(row, car);
    } else if (car) {
      this.renderCar(car);
    } else {
      this.renderEmpty();
    }
  }

  row(txt, cls = '') {
    return `<div class="city-row${cls ? ' ' + cls : ''}">${txt}</div>`;
  }

  renderStages(row) {
    const idx = Number(row.stage) || 0;
    this.el.stages.innerHTML = STAGES.map((st, i) => {
      const state = i < idx ? 'done' : (i === idx ? 'now' : 'todo');
      const mark = i < idx ? '已装' : (i === idx ? '正在装' : '待装');
      return this.row(
        `<span>${i + 1}. ${st.name}</span>
         <span class="city-tag ${state === 'now' ? 'warn' : 'ok'}">${mark}</span>`,
        state === 'now' ? 'now' : '',
      );
    }).join('');
  }

  renderNeed(row) {
    const idx = Number(row.stage) || 0;
    const st = STAGES[idx];
    if (!st) {
      this.el.need.innerHTML = this.row('整车已经攒齐了。', 'ok');
      return;
    }
    const lack = Assemble.missing(this.db, idx);
    const lines = Object.entries(st.need).map(([id, n]) => {
      const has = Repo.countItem(this.db, id);
      const okIcon = has >= n ? '<span class="city-tag ok">够</span>' : `<span class="city-tag warn">还差${n - has}</span>`;
      return this.row(`<span>${getItem(id).name} ${has}/${n}</span>${okIcon}`);
    });
    // 后面几段一共还要什么，心里有个数
    const rest = remainingNeed(idx + 1);
    const restTxt = Object.entries(rest).map(([id, n]) => `${getItem(id).name}×${n}`).join('、');
    this.el.need.innerHTML = [
      `<div class="tips">这一段：${st.says}</div>`,
      ...lines,
      restTxt ? `<div class="tips">后面几段还要：${restTxt}</div>` : '',
      lack.length ? `<div class="tips">缺：${lack.join('、')}</div>` : `<div class="tips">这一段的料齐了。</div>`,
    ].join('');
  }

  renderActs(row, car) {
    const idx = Number(row.stage) || 0;
    // 正抡着扳手就不给别的按：连点只会浪费一次快手
    if (this.busy) {
      this.el.acts.innerHTML = [
        `<button class="btn" disabled>正在装${this.busy.stage.name}…</button>`,
        `<button class="btn" data-act="stop">住手</button>`,
      ].join('');
      return;
    }
    const lack = Assemble.missing(this.db, idx);
    const p = Repo.getPlayer(this.db);
    const tired = (p.energy || 0) < 12;
    const rate = Math.round(Assemble.failRate(this.db, this.bench) * 100);
    this.el.acts.innerHTML = [
      lack.length
        ? `<button class="btn" disabled>装${STAGES[idx].name}（缺料）</button>`
        : (tired
          ? `<button class="btn" disabled>装${STAGES[idx].name}（手抬不动了）</button>`
          : `<button class="btn" data-act="install">装${STAGES[idx].name}（失手 ${rate}%）</button>`),
      `<button class="btn" data-act="scrap">拆了重攒</button>`,
    ].join('');
    void car;
  }

  renderCar(car) {
    const def = Vehicle.def(car);
    const maxF = Number(car.maxFuel) || def.maxFuel;
    const maxD = Number(car.maxDur) || def.maxDur;
    const fuel = Number(car.fuel || 0);
    const dur = Number(car.durability || 0);
    this.el.stages.innerHTML = [
      this.row(`<span>${car.name}</span><span class="city-tag ok">${def.name}</span>`),
      this.row(`<span>油箱 ${Math.round(fuel)}/${maxF}</span><span class="city-tag ${fuel / maxF < 0.2 ? 'warn' : 'ok'}">${Math.round(fuel / maxF * 100)}%</span>`),
      this.row(`<span>车况 ${Math.round(dur)}/${maxD}</span><span class="city-tag ${dur / maxD < 0.2 ? 'warn' : 'ok'}">${Math.round(dur / maxD * 100)}%</span>`),
      this.row(`<span>里程 ${Math.round(Number(car.odo || 0))} 格</span><span class="city-tag ${car.broken ? 'warn' : 'ok'}">${car.broken ? '趴窝' : '能跑'}</span>`),
      `<div class="tips">${def.desc}</div>`,
    ].join('');

    const up = def.upgrade;
    const upDef = up ? VEHICLE_DEFS[up.to] : null;
    const lackUp = up ? Object.entries(up.need).filter(([id, n]) => Repo.countItem(this.db, id) < n) : [];
    const cash = Math.round(Repo.getPlayer(this.db).cash || 0);
    const upTxt = up
      ? `改造到${upDef.name}：${up.cash} 块钱 + ${Object.entries(up.need).map(([id, n]) => `${getItem(id).name}×${n}`).join('、')}`
      : '已经到顶了，没什么可改。';
    this.el.need.innerHTML = [
      `<div class="tips">${upTxt}</div>`,
      up && lackUp.length ? `<div class="tips">缺料：${lackUp.map(([id, n]) => `${getItem(id).name}×${n}`).join('、')}</div>` : '',
      up && cash < up.cash ? `<div class="tips">还差 ${up.cash - cash} 块钱手工钱。</div>` : '',
    ].join('');

    this.el.acts.innerHTML = [
      `<button class="btn" data-act="refuel">加油</button>`,
      `<button class="btn" data-act="repair">修车</button>`,
      up ? `<button class="btn" data-act="upgrade">改造成${upDef.name}</button>` : '',
    ].join('');
  }

  renderEmpty() {
    const need = totalNeed();
    const list = Object.entries(need).map(([id, n]) => {
      const has = Repo.countItem(this.db, id);
      return this.row(`<span>${getItem(id).name} ${has}/${n}</span><span class="city-tag ${has >= n ? 'ok' : 'warn'}">${has >= n ? '够' : '不够'}</span>`);
    });
    this.el.stages.innerHTML = [
      `<div class="tips">一辆车要抡五遍扳手：底盘、动力、转向、电气、总装。最难凑的不是铁皮木片，是车架、引擎和电瓶——这几样得靠废车堆里翻。</div>`,
    ].join('');
    this.el.need.innerHTML = list.join('') + `<div class="tips">一共 ${STAGE_COUNT} 段，每段各自吞各自的料。</div>`;
    const p = Repo.getPlayer(this.db);
    const canBegin = Object.entries(need).every(([id, n]) => Repo.countItem(this.db, id) >= n);
    void p;
    this.el.acts.innerHTML = canBegin
      ? `<button class="btn" data-act="begin">支上架子开工</button>`
      : `<button class="btn" disabled>支上架子开工（料还不齐）</button>`;
  }
}
